// Fixed DashboardPage.jsx with correct UserTradePerformance import

import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { 
  AccountCircle, 
  TrendingUp, 
  History,
  Assessment,
  CalendarToday
} from '@mui/icons-material';
import authService from '../services/authService';
import tradeService from '../services/tradeService';
import UserTradeHistory from '../components/UserTradeHistory';
import UserTradePerformance from '../components/UserTradePerformance'; // ✅ FIXED: Correct import path
import ActiveTrades from '../components/ActiveTrades';

const DashboardPage = () => {
  const [user, setUser] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }
        
        setUser(currentUser);
        
        try {
          // Get user-specific performance metrics
          const metricsResponse = await tradeService.getPerformanceMetrics('user');
          setMetrics(metricsResponse.metrics);
        } catch (err) {
          console.warn('Could not load metrics', err);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading dashboard data', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Your Trading Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.first_name}! Here's your personal trading overview.
          </Typography>
          <Chip 
            size="small" 
            label={`${user?.subscription_plan.charAt(0).toUpperCase() + user?.subscription_plan.slice(1)} Plan`}
            color={user?.subscription_plan === 'standard' ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Quick Stats Overview */}
      {metrics && metrics.totalTrades > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography color="text.secondary" gutterBottom>
                    Total Trades
                  </Typography>
                  <TrendingUp color="primary" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {metrics.totalTrades}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography color="text.secondary" gutterBottom>
                    Win Rate
                  </Typography>
                  <Assessment color={parseFloat(metrics.winRate) >= 50 ? "success" : "error"} />
                </Box>
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  color={parseFloat(metrics.winRate) >= 50 ? "success.main" : "error.main"}
                >
                  {metrics.winRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography color="text.secondary" gutterBottom>
                    Total Return
                  </Typography>
                  <TrendingUp color={parseFloat(metrics.totalProfitPercentage) >= 0 ? "success" : "error"} />
                </Box>
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  color={parseFloat(metrics.totalProfitPercentage) >= 0 ? "success.main" : "error.main"}
                >
                  {parseFloat(metrics.totalProfitPercentage) >= 0 ? "+" : ""}{metrics.totalProfitPercentage}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography color="text.secondary" gutterBottom>
                    Total Pips
                  </Typography>
                  <TrendingUp color={parseFloat(metrics.totalPips) >= 0 ? "success" : "error"} />
                </Box>
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  color={parseFloat(metrics.totalPips) >= 0 ? "success.main" : "error.main"}
                >
                  {parseFloat(metrics.totalPips) >= 0 ? "+" : ""}{metrics.totalPips}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
            <Tab icon={<TrendingUp />} iconPosition="start" label="Active Trades" />
            <Tab icon={<History />} iconPosition="start" label="Your Trade History" />
            <Tab icon={<Assessment />} iconPosition="start" label="Your Performance" />
          </Tabs>
        </Box>
        
        <Box role="tabpanel" hidden={tabValue !== 0}>
          {tabValue === 0 && <ActiveTrades />}
        </Box>
        
        <Box role="tabpanel" hidden={tabValue !== 1}>
          {tabValue === 1 && <UserTradeHistory />}
        </Box>
        
        <Box role="tabpanel" hidden={tabValue !== 2}>
          {tabValue === 2 && (
            metrics ? (
              <UserTradePerformance />
            ) : (
              <Alert severity="info">
                No performance data available yet. Start trading to see your performance metrics.
              </Alert>
            )
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default DashboardPage;